import { Alert, Button, Card, SkeletonRows, StatusBadge } from "@station/ui";
import { createFileRoute } from "@tanstack/react-router";
import { RefreshCw } from "lucide-react";
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { formatBytes } from "../../features/platform/platform-values.js";
import { usePlatformHealth } from "../../features/platform/queries.js";
import { PlatformPage } from "../../features/platform/shared.js";
import { Section } from "../../features/platform/tenant-tabs.js";
import { errorMessage } from "../../lib/errors.js";
import { formatCount, formatDateTime } from "../../lib/format.js";

export const Route = createFileRoute("/platform/saglik")({ component: HealthRoute });

const th = "py-1 pe-2 text-left text-xs font-medium text-muted-foreground";
const thRight = "py-1 pe-2 text-right text-xs font-medium text-muted-foreground";
const td = "py-1 pe-2";
const tdRight = "py-1 pe-2 text-right tabular";

/**
 * Sistem sağlığı (Prompt 24 web): iş kuyrukları ve başarısız işler, outbox gecikmesi, kiracı bazında e-belge hataları,
 * çevrimdışı ajanlar ve veritabanı boyutu. Dakikada bir tazelenir; kiracı verisinin içeriği değil yalnızca sayılar görünür.
 */
function HealthRoute(): ReactElement {
  const { t } = useTranslation();
  const health = usePlatformHealth();
  const data = health.data;

  return (
    <PlatformPage
      title={t("platform.health.title")}
      description={t("platform.health.description")}
      permission="platform.health.read"
      actions={
        <Button variant="outline" loading={health.isFetching} onClick={() => void health.refetch()}>
          <RefreshCw aria-hidden="true" />
          {t("platform.health.refresh")}
        </Button>
      }
    >
      {health.error ? (
        <Alert tone="danger">{errorMessage(health.error)}</Alert>
      ) : health.isPending || !data ? (
        <SkeletonRows rows={8} />
      ) : (
        <>
          <p className="text-sm text-muted-foreground">
            {t("platform.health.generatedAt", { time: formatDateTime(data.generatedAt) })}
          </p>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <Metric
              label={t("platform.health.outboxPending")}
              value={formatCount(data.outbox.pending)}
              warn={data.outbox.lagSeconds > 300}
            />
            <Metric
              label={t("platform.health.outboxLag")}
              value={t("platform.health.seconds", { count: data.outbox.lagSeconds })}
              warn={data.outbox.lagSeconds > 300}
            />
            <Metric
              label={t("platform.health.offlineAgents")}
              value={formatCount(data.offlineAgents.total)}
              warn={data.offlineAgents.total > 0}
            />
            <Metric label={t("platform.health.databaseSize")} value={formatBytes(data.database.sizeBytes)} />
          </div>

          <Section title={t("platform.health.queues")}>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <caption className="sr-only">{t("platform.health.queues")}</caption>
                <thead>
                  <tr className="border-b">
                    <th scope="col" className={th}>
                      {t("platform.fields.queue")}
                    </th>
                    <th scope="col" className={thRight}>
                      {t("platform.health.waiting")}
                    </th>
                    <th scope="col" className={thRight}>
                      {t("platform.health.active")}
                    </th>
                    <th scope="col" className={thRight}>
                      {t("platform.health.delayed")}
                    </th>
                    <th scope="col" className={thRight}>
                      {t("platform.health.failed")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.queues.map((queue) => (
                    <tr key={queue.name} className="border-b last:border-0">
                      <td className={`${td} font-mono`}>{queue.name}</td>
                      <td className={tdRight}>{formatCount(queue.waiting)}</td>
                      <td className={tdRight}>{formatCount(queue.active)}</td>
                      <td className={tdRight}>{formatCount(queue.delayed)}</td>
                      <td
                        className={`${tdRight} ${queue.failed > 0 ? "font-semibold text-destructive" : ""}`}
                      >
                        {formatCount(queue.failed)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>

          <Section title={t("platform.health.failedJobs")}>
            {data.failedJobs.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t("platform.health.noFailedJobs")}</p>
            ) : (
              <ul className="grid gap-2 text-sm">
                {data.failedJobs.map((job) => (
                  <li key={`${job.queue}-${job.id}`} className="grid gap-0.5 border-b pb-2 last:border-0">
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="font-mono">{job.queue}</span>
                      <span className="font-medium">{job.name}</span>
                      <StatusBadge tone="danger">
                        {t("platform.health.attempts", { count: job.attemptsMade })}
                      </StatusBadge>
                      {job.finishedAt ? (
                        <span className="text-muted-foreground">{formatDateTime(job.finishedAt)}</span>
                      ) : null}
                    </span>
                    {job.failedReason ? (
                      <span className="break-all text-destructive">{job.failedReason}</span>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </Section>

          <div className="grid gap-4 lg:grid-cols-2">
            <Section title={t("platform.health.edocumentErrors")}>
              {data.edocumentErrors.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t("platform.health.noEdocumentErrors")}</p>
              ) : (
                <ul className="grid gap-1 text-sm">
                  {data.edocumentErrors.map((row) => (
                    <li key={row.tenant.id} className="flex flex-wrap justify-between gap-2">
                      <span>{row.tenant.name}</span>
                      <span className="text-muted-foreground">
                        {t("platform.health.edocumentLine", {
                          invoices: row.invoices,
                          despatches: row.despatches,
                        })}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </Section>
            <Section title={t("platform.health.offlineAgents")}>
              {data.offlineAgents.items.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t("platform.health.noOfflineAgents")}</p>
              ) : (
                <ul className="grid gap-1 text-sm">
                  {data.offlineAgents.items.map((agent) => (
                    <li
                      key={`${agent.tenant.id}-${agent.hostname}`}
                      className="flex flex-wrap justify-between gap-2"
                    >
                      <span>
                        {agent.tenant.name} · <span className="font-mono">{agent.stationCode}</span> ·{" "}
                        {agent.hostname}
                      </span>
                      <span className="text-muted-foreground">
                        <span className="font-mono">{agent.version}</span> ·{" "}
                        {agent.lastSeenAt ? formatDateTime(agent.lastSeenAt) : t("platform.health.neverSeen")}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </Section>
          </div>

          <Section title={t("platform.health.tables")}>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <caption className="sr-only">{t("platform.health.tables")}</caption>
                <thead>
                  <tr className="border-b">
                    <th scope="col" className={th}>
                      {t("platform.fields.table")}
                    </th>
                    <th scope="col" className={thRight}>
                      {t("platform.fields.size")}
                    </th>
                    <th scope="col" className={thRight}>
                      {t("platform.fields.estimatedRows")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.database.tables.map((table) => (
                    <tr key={table.name} className="border-b last:border-0">
                      <td className={`${td} font-mono`}>{table.name}</td>
                      <td className={tdRight}>{formatBytes(table.totalBytes)}</td>
                      <td className={tdRight}>{formatCount(Number(table.estimatedRows))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>
        </>
      )}
    </PlatformPage>
  );
}

function Metric({ label, value, warn }: { label: string; value: string; warn?: boolean }): ReactElement {
  return (
    <Card className={`grid gap-1 p-4 ${warn ? "border-warning/50" : ""}`}>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={`text-2xl font-semibold tabular ${warn ? "text-warning" : ""}`}>{value}</p>
    </Card>
  );
}
